import { useState, useMemo } from "react";
import { Box, IconButton, Popover, Typography, Tabs, Tab } from "@mui/material";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";

const EMOJI_DATA = {
  "😀": [
    "😀","😃","😄","😁","😆","😅","🤣","😂","🙂","😉",
    "😊","😇","🥰","😍","🤩","😘","😗","😚","😙","🥲",
    "😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","🤔",
    "😐","😑","😶","😏","😒","🙄","😬","🤥","😌","😔",
    "😪","🤤","😴","😷","🤒","🤕","🤢","🤮","🥵","🥶",
    "🥴","😵","🤯","🤠","🥳","🥸","😎","🤓","🧐","😕",
    "😟","🙁","😮","😯","😲","😳","🥺","😦","😧","😨",
    "😰","😥","😢","😭","😱","😖","😣","😞","😓","😩",
    "😤","😡","😠","🤬","💀","☠️","💩","🤡","👹","👺",
  ],
  "👋": [
    "👋","🤚","🖐️","✋","🖖","👌","🤌","🤏","✌️","🤞",
    "🤟","🤘","🤙","👈","👉","👆","🖕","👇","☝️","👍",
    "👎","✊","👊","🤛","🤜","👏","🙌","👐","🤲","🤝",
    "🙏","✍️","💪","🦾","🦿","🦵","🦶","👂","🦻","👃",
    "👶","👧","🧒","👦","👩","🧑","👨","👵","🧓","👴",
  ],
  "❤️": [
    "❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔",
    "❣️","💕","💞","💓","💗","💖","💘","💝","💟","💌",
    "💋","👄","👅","🫶","🫂","💏","💑","🌹","🌸","🌺",
  ],
  "🐶": [
    "🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐨","🐯",
    "🦁","🐮","🐷","🐸","🐵","🙈","🙉","🙊","🐒","🐔",
    "🐧","🐦","🐤","🐣","🐥","🦆","🦅","🦉","🦇","🐺",
    "🐗","🐴","🦄","🐝","🪱","🐛","🦋","🐌","🐞","🐜",
  ],
  "🍕": [
    "🍕","🍔","🍟","🌭","🍿","🧂","🥨","🥯","🍞","🧀",
    "🥚","🍳","🥞","🧇","🥓","🥩","🍗","🍖","🌮","🌯",
    "🥗","🍝","🍜","🍲","🍛","🍣","🍱","🥟","🦪","🍤",
    "🍙","🍚","🍘","🍥","🥠","🥮","🍢","🍡","🍧","🍨",
    "🍦","🥧","🧁","🍰","🎂","🍮","🍭","🍬","🍫","🍩",
    "☕","🍵","🧃","🥤","🧋","🍶","🍺","🍻","🥂","🍷",
  ],
  "⚽": [
    "⚽","🏀","🏈","⚾","🥎","🎾","🏐","🏉","🥏","🎱",
    "🏓","🏸","🏒","🥅","⛳","🏹","🎣","🤿","🥊","🥋",
    "🎽","🛹","🛼","🛷","⛸️","🥌","🎿","⛷️","🏋️","🤸",
  ],
  "🚗": [
    "🚗","🚕","🚙","🚌","🚎","🏎️","🚓","🚑","🚒","🚐",
    "🛻","🚚","🚛","🚜","✈️","🛫","🛬","🚀","🛸","🚁",
    "⛵","🚤","🛥️","🛳️","⛴️","🚢","🏠","🏡","🏢","🏗️",
  ],
  "💡": [
    "💡","🔦","🕯️","🔥","💥","⭐","🌟","✨","⚡","☀️",
    "🌈","🎉","🎊","🎈","🎁","🏆","🥇","🥈","🥉","🎵",
    "🎶","🎤","🎧","📱","💻","⌨️","📷","📹","📺","📻",
    "⏰","🔔","📣","💰","💎","🔑","🗝️","🔒","📌","📎",
    "✏️","✒️","📝","📚","📖","🔍","🔎","💯","✅","❌",
    "⭕","❗","❓","⚠️","🚫","♻️","🏳️","🏴","🚩","🔴",
    "🟠","🟡","🟢","🔵","🟣","⚫","⚪","🟤","🔶","🔷",
  ],
};

const CATEGORY_LABELS = {
  "😀": "Smileys",
  "👋": "People",
  "❤️": "Love",
  "🐶": "Animals",
  "🍕": "Food",
  "⚽": "Sports",
  "🚗": "Travel",
  "💡": "Objects",
};

const categories = Object.keys(EMOJI_DATA);

export const EmojiPicker = ({ onSelect, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tab, setTab] = useState(0);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (emoji) => {
    onSelect(emoji);
    handleClose();
  };

  const currentEmojis = useMemo(() => EMOJI_DATA[categories[tab]] || [], [tab]);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        disabled={disabled}
        aria-label="Emoji"
        sx={{
          color: open ? "primary.main" : "text.secondary",
          width: 36,
          height: 36,
          "&:hover": { color: "primary.main" },
        }}
      >
        <EmojiEmotionsOutlinedIcon sx={{ fontSize: 22 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              height: 360,
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            },
          },
        }}
      >
        {/* Category tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 40,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            "& .MuiTab-root": {
              minWidth: 36,
              minHeight: 40,
              p: 0.5,
              fontSize: 18,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "primary.main",
            },
          }}
        >
          {categories.map((cat) => (
            <Tab key={cat} label={cat} title={CATEGORY_LABELS[cat]} />
          ))}
        </Tabs>

        {/* Emoji grid */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 1,
            display: "grid",
            gridTemplateColumns: "repeat(8, 1fr)",
            gap: 0.25,
            alignContent: "start",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 3,
              backgroundColor: "rgba(0,0,0,0.15)",
            },
          }}
        >
          {currentEmojis.map((emoji) => (
            <Box
              key={emoji}
              onClick={() => handleSelect(emoji)}
              sx={{
                fontSize: 22,
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "6px",
                cursor: "pointer",
                userSelect: "none",
                transition: "background 100ms",
                "&:hover": {
                  backgroundColor: "rgba(7,31,40,0.06)",
                  transform: "scale(1.15)",
                },
              }}
            >
              {emoji}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};
